import { modalController } from '@ionic/core';
import { Component, Event, EventEmitter, Fragment, h, Prop, State } from '@stencil/core';
import { DrawingPageDoc, FilePageDoc, TagData } from 'common';
import { Tag, User } from 'src/global/models';
import { FileService, PageAnalysis } from 'src/global/service/FileService';
import { ConcourseTagSelector } from '../concourse-tag-selector/concourse-tag-selector';

@Component({
  tag: 'drawing-page-settings-modal',
})
export class DrawingPageSettingsModal {
  static async present(projectId: string, page: DrawingPageDoc): Promise<DrawingPageDoc> {
    const modal = await modalController.create({
      component: 'drawing-page-settings-modal',
      componentProps: { projectId, page },
      backdropDismiss: false,
      cssClass: 'modal-height-auto',
    });
    await modal.present();

    return new Promise(async resolve => {
      modal.addEventListener('save', (e: CustomEvent) => {
        resolve(e.detail as DrawingPageDoc);
        modal.dismiss();
      });

      modal.addEventListener('dismiss', () => {
        resolve(null);
        modal.dismiss();
      });
    });
  }

  @Prop() projectId: string;
  //@Prop() fileId: string;
  @Prop() page: DrawingPageDoc;

  @State() modified: DrawingPageDoc;
  @State() isAnalysing: boolean;

  @Event() dismiss: EventEmitter;
  @Event() save: EventEmitter<FilePageDoc>;

  get user(): User {
    return globalThis.user;
  }

  async componentWillLoad() {
    this.modified = { ...this.page };
  }

  async showTagSelector() {
    ConcourseTagSelector.presentModal(this.projectId, '', async (tag: TagData) => {
      this.modified.tags = this.page.tags || [];
      this.modified.tags.push(tag);
    });
  }

  async analyseFile() {
    this.isAnalysing = true;
    const analysis: PageAnalysis[] = await FileService.analyseFilePages(this.projectId, this.page.file_id, [this.page._id], this.page.drawing_num);
    if (analysis && analysis.length == 1) {
      this.modified.title = analysis[0].title;
      this.modified.description = analysis[0].description;
      this.modified.drawing_num = analysis[0].drawingNum;
      this.modified.aiWarnings = (analysis[0].warnings ?? []).map(w => String(w).trim()).filter(Boolean);

      const projectTags = await Tag.getTags(this.projectId);
      this.modified.tags = projectTags.filter(tag => analysis[0].tags.includes(tag.name));
    }
    this.isAnalysing = false;
  }

  removeTag(tag: TagData) {
    this.modified.tags = this.modified.tags.filter(t => t.name != tag.name);
  }

  onSaveClick() {
    this.save.emit(this.modified);
  }

  render() {
    return (
      <Fragment>
        <ion-header>
          <ion-toolbar>
            <ion-title>Edit Page {this.page._id}</ion-title>
            <ion-buttons slot="end">
              <ion-button onclick={() => this.dismiss.emit()}>
                <ion-icon name="close" />
              </ion-button>
            </ion-buttons>
          </ion-toolbar>
        </ion-header>
        <ion-page class="ion-padding">
          <ion-input label="Title" label-placement="stacked" class="cc-margin-bottom" value={this.modified.title} onIonInput={e => (this.modified.title = e.detail.value)} />
          <ion-textarea
            id="drawing-num-desc"
            label="Description"
            label-placement="stacked"
            class="cc-margin-bottom"
            rows="6"
            value={this.modified.description}
            onIonInput={e => (this.modified.description = e.detail.value)}
          />
          {this.modified.aiWarnings?.length > 0 ? (
            <ion-textarea
              id="drawing-num-warnings"
              label="AI-identified warnings"
              label-placement="stacked"
              class="cc-margin-bottom"
              rows="3"
              value={this.modified.aiWarnings.join('\n')}
              onIonInput={e =>
                (this.modified.aiWarnings = (e.detail.value ?? '')
                  .split('\n')
                  .map(s => s.trim())
                  .filter(Boolean))
              }
            />
          ) : null}
          <ion-input
            id="drawing-num-input"
            label="drawing num"
            label-placement="stacked"
            class="cc-margin-bottom"
            value={this.modified.drawing_num}
            onIonInput={e => (this.modified.drawing_num = e.detail.value)}
          />
          <div class="button-row">
            <span>Tags:</span>
            {this.modified.tags?.map((tag: TagData) => (
              <concourse-tag projectId={this.projectId} data={tag} editable={true} onRemove={e => this.removeTag(tag)} />
            ))}
            <ion-button fill="clear" onClick={() => this.showTagSelector()}>
              <ion-icon name="add-circle-outline"></ion-icon>
            </ion-button>
          </div>
        </ion-page>
        <ion-footer class="ion-padding-horizontal">
          <ion-toolbar>
            <ion-button slot="start" disabled={this.isAnalysing} fill="outline" onClick={e => this.analyseFile()}>
              {this.isAnalysing ? 'Analysing, please wait...' : 'Analyse with AI'}
            </ion-button>
            <ion-button slot="end" onClick={() => this.onSaveClick()}>
              Save
            </ion-button>
          </ion-toolbar>
        </ion-footer>
      </Fragment>
    );
  }
}
